import { useEffect, useRef, useState, type FormEvent } from "react";
import { useParams } from "react-router";
import { linkProblem, reviewProblem, roundsNote } from "@talentkontor/domain";
import { api, ApiError } from "../../data";
import { AgenturKopf, BriefingAnsicht, MarkenRahmen, TextMitLinks, useMarkenSprache } from "./MarkeBausteine";
import { T, decisionProblem, fileSize, fmtDate, parseReviewComments, parseReviewView, tokenOk, type Lang, type ReviewFile, type ReviewMedia, type ReviewView } from "./marke-model";
import { FreigabeMedien, type MedienLink } from "./FreigabeMedien";
import { fehlerText } from "../../lib/fehler";

/**
 * Freigabe-Link der Marke unter /freigabe/:token (0073): Entwurf mit Dateien, Briefing, Nachricht der Agentur; „Freigeben“
 * oder „Änderung wünschen“ mit Kommentar. Mobil zuerst, hell und dunkel, Sprache der Marke (umschaltbar). Das Öffnen wird
 * genau einmal geladen und protokolliert (keine Abfrage, die sich neu lädt). Keine Beträge, Konditionen oder Notizen.
 */
export function Freigabe() {
  const { token = "" } = useParams();
  return <FreigabeSeite key={token} token={token} />;
}

function FreigabeSeite({ token }: { token: string }) {
  const [view, setView] = useState<ReviewView | null>(null);
  const [problem, setProblem] = useState<string | null>(tokenOk(token) ? null : "link:invalid");
  const loaded = useRef(false);
  const [lang, setLang] = useMarkenSprache(view?.language ?? null);
  useEffect(() => {
    if (!tokenOk(token) || loaded.current) return;
    loaded.current = true;
    api.rpcAsAnon<unknown>("brand_review_open", { p_token: token })
      .then((r) => setView(parseReviewView(r)))
      .catch((e: unknown) => setProblem(e instanceof ApiError && e.hint ? e.hint : "other"));
  }, [token]);
  const t = T[lang];
  if (problem || !view) {
    const p = problem ? linkProblem(problem, lang) : null;
    return (
      <MarkenRahmen lang={lang} onLang={setLang} agency={null}>
        <title>{`${p?.title ?? t.loading} · CreatorOffice`}</title>
        <main id="main" className="auth-shell stack" aria-busy={!problem}>
          {/* Karte wie die übrigen öffentlichen Zustandsseiten (Link abgelaufen, Kit ungültig) */}
          <section className="centerform panel stack">
            <h1>{p?.title ?? t.loading}</h1>
            {p && <p>{p.text}</p>}
          </section>
        </main>
      </MarkenRahmen>
    );
  }
  return (
    <MarkenRahmen lang={lang} onLang={setLang} agency={view.agency}>
      <title>{`${view.item ? t.title(view.item.label) : t.draftTitle} · ${view.agency.name}`}</title>
      <main id="main" className="tk-marke stack">
        <AgenturKopf agency={view.agency} />
        <FreigabeAnsicht view={view} lang={lang} onFile={(f) => api.linkFile("review", token, f.id)}
          onMedia={(m, download) => api.medien<MedienLink>({ action: "brand_url", token, media: m.id, download })}
          onComment={async (m, k, name) => {
            const r = await api.rpcAsAnon<{ comments?: unknown }>("brand_review_comment", { p_token: token, p_media: m.id, p_body: k.body, p_name: name.trim(),
              p_time_ms: k.time_ms, p_x: k.x, p_y: k.y });
            // ohne erneutes Öffnen (zählte sonst als Aufruf der Marke)
            setView((v) => (v ? { ...v, comments: parseReviewComments(r?.comments) } : v));
          }}
          onDecide={async (input) => {
            try {
              const r = await api.rpcAsAnon<unknown>("brand_review_decide", {
                p_token: token, p_approve: input.approve, p_comment: input.comment.trim() || null, p_name: input.name.trim(), p_extra_ack: input.extraAck,
              });
              setView(parseReviewView(r));
            } catch (e) {
              // Inzwischen anders entschieden (von jemand anderem über den Link oder vom Team): aktuellen Stand zeigen
              if (e instanceof ApiError && (e.hint === "review:decided" || e.hint === "review:closed")) {
                await api.rpcAsAnon<unknown>("brand_review_open", { p_token: token }).then((r) => setView(parseReviewView(r))).catch(() => undefined);
              }
              throw e;
            }
          }} />
        {api.mode === "demo" && <p className="demo-note">{lang === "en" ? "Demo · All data is fictitious." : "Demo · Alle Daten sind erfunden."}</p>}
      </main>
    </MarkenRahmen>
  );
}

export interface DecisionInput {
  approve: boolean;
  name: string;
  comment: string;
  extraAck: boolean;
}

/** Inhalt des Freigabe-Links; auch als Vorschau im Agentur-Bereich (ohne onDecide: keine Entscheidung möglich). */
export function FreigabeAnsicht({ view, lang, onFile, onDecide, onMedia, onComment, embedded = false }: {
  view: ReviewView; lang: Lang; onFile: (f: ReviewFile) => Promise<Blob>; onDecide?: (input: DecisionInput) => Promise<void>; embedded?: boolean;
  /** Content-Ablage (0155): signierter Link zur Datei (Ansehen bzw. Herunterladen) */
  onMedia?: (m: ReviewMedia, download: boolean) => Promise<MedienLink>;
  /** Kommentar der Marke mit Zeitmarke oder Punkt; nur solange sie entscheiden kann */
  onComment?: (m: ReviewMedia, k: { body: string; time_ms: number | null; x: number | null; y: number | null }, name: string) => Promise<void>;
}) {
  const t = T[lang];
  const [name, setName] = useState(view.recipient_name ?? "");
  const [kommentarFehler, setKommentarFehler] = useState<string | null>(null);
  const offen = view.state === "open" && !view.decision;
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!embedded) heading.current?.focus();
  }, [embedded]);
  const titel = view.item ? t.title(view.item.label) : t.draftTitle;
  return (
    <>
      <div className="stack">
        <div className="eyebrow">{t.eyebrow(view.agency.name)}</div>
        {embedded ? <h3>{titel}</h3> : <h1 ref={heading} tabIndex={-1}>{titel}</h1>}
        <p>{t.sub(view.creator, view.deal_title, view.brand)}</p>
        <p className="source-note">
          {[view.item?.draft_due ? `${t.draftDue} ${fmtDate(view.item.draft_due, lang)}` : null,
            view.item?.publish_on ? `${t.postOn} ${fmtDate(view.item.publish_on, lang)}` : null,
            view.state === "open" ? `${t.validUntil} ${fmtDate(view.expires_at, lang)}` : null].filter(Boolean).join(" · ")}
        </p>
      </div>
      {view.team_view && <div className="notice" role="status">{t.teamView}</div>}
      {view.message && (
        <section className="panel stack" aria-label={t.message(view.agency.name)}>
          <strong>{t.message(view.agency.name)}</strong>
          <TextMitLinks text={view.message} />
        </section>
      )}
      <section className="panel stack" aria-labelledby="freigabe-entwurf">
        <h2 id="freigabe-entwurf">{t.draft}</h2>
        <p className="source-note">{t.version(view.submission.version, fmtDate(view.submission.submitted_at, lang, true))}</p>
        {view.submission.caption && <TextMitLinks text={view.submission.caption} />}
        {view.submission.files.length > 0
          ? <div className="stack"><strong>{t.files}</strong>{view.submission.files.map((f) => <DateiZeile key={f.id} file={f} lang={lang} load={onFile} />)}</div>
          : !view.submission.caption && !view.submission.media.length && <p className="muted">{t.noFiles}</p>}
        {view.submission.team_approved && <p className="source-note">{lang === "en" ? "This version has already been approved by the agency." : "Diese Fassung hat die Agentur bereits freigegeben."}</p>}
      </section>
      {view.submission.media.length > 0 && onMedia && (
        <FreigabeMedien media={view.submission.media} comments={view.comments} lang={lang} holen={onMedia} name={name} setName={setName}
          onKommentar={onComment && offen ? async (m, k) => {
            try {
              await onComment(m, k, name);
              setKommentarFehler(null);
              return true;
            } catch (err) {
              const hint = err instanceof ApiError ? err.hint : undefined;
              setKommentarFehler(hint?.startsWith("link:") || hint === "org:read_only" ? linkProblem(hint, lang).title : fehlerText(err, lang));
              return false;
            }
          } : undefined} />
      )}
      {kommentarFehler && <p className="tk-error" role="alert">{kommentarFehler}</p>}
      <section className="panel stack" aria-labelledby="freigabe-briefing">
        <h2 id="freigabe-briefing">{lang === "en" ? "Briefing" : "Briefing"}</h2>
        <BriefingAnsicht briefing={view.briefing} lang={lang} leer={lang === "en" ? "No briefing yet." : "Kein Briefing hinterlegt."} />
      </section>
      {view.history.length > 0 && (
        <section className="panel stack" aria-labelledby="freigabe-verlauf">
          <h2 id="freigabe-verlauf">{t.history}</h2>
          {view.history.map((h, i) => (
            <div key={i} className="stack">
              <strong>{t.historyRow(h.version, h.status)} · {fmtDate(h.at, lang)}{h.name ? ` · ${h.name}` : ""}</strong>
              {h.comment && <TextMitLinks text={h.comment} />}
            </div>
          ))}
        </section>
      )}
      <Entscheidung view={view} lang={lang} onDecide={onDecide} name={name} setName={setName} />
      <p className="source-note">{t.evidence} {t.ref}: <span className="tk-mono">{view.content_sha256.slice(0, 16)}…</span></p>
    </>
  );
}

function Entscheidung({ view, lang, onDecide, name, setName }: {
  view: ReviewView; lang: Lang; onDecide?: (input: DecisionInput) => Promise<void>; name: string; setName: (n: string) => void;
}) {
  const t = T[lang];
  const [comment, setComment] = useState("");
  const [extraAck, setExtraAck] = useState(false);
  const [busy, setBusy] = useState<"approve" | "changes" | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  if (view.decision) {
    return (
      <section className="panel stack" role="status" aria-labelledby="freigabe-ergebnis">
        <h2 id="freigabe-ergebnis">{view.decision.status === "approved" ? t.doneApproved : t.doneChanges}</h2>
        <p>{t.decided(view.decision.status, view.decision.name, fmtDate(view.decision.at, lang, true))}</p>
        {view.decision.comment && <TextMitLinks text={view.decision.comment} />}
        <p>{t.doneText(view.agency.name)}</p>
      </section>
    );
  }
  if (view.state === "closed" || view.state === "withdrawn") return <div className="notice" role="status">{t.stateClosed}</div>;
  if (view.state === "expired") return <div className="notice" role="status">{t.stateExpired}</div>;
  const note = roundsNote(view.rounds, lang);
  const senden = async (approve: boolean, e?: FormEvent) => {
    e?.preventDefault();
    if (!onDecide || busy) return;
    const p = decisionProblem({ approve, name, comment, extraAck }, view.rounds, lang);
    if (p) { setFehler(p); return; }
    setFehler(null);
    setBusy(approve ? "approve" : "changes");
    try {
      await onDecide({ approve, name, comment, extraAck });
    } catch (err) {
      const hint = err instanceof ApiError ? err.hint : undefined;
      setFehler(hint?.startsWith("link:") || hint === "org:read_only" ? linkProblem(hint, lang).title
        : reviewProblem(hint, lang) ?? (lang === "en" ? "The decision could not be saved. Please try again." : err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(null);
    }
  };
  return (
    <form className="panel stack" aria-labelledby="freigabe-entscheidung" onSubmit={(e) => void senden(true, e)} noValidate>
      <h2 id="freigabe-entscheidung">{t.decide}</h2>
      <label>{t.name}
        <input value={name} maxLength={120} autoComplete="name" placeholder={t.namePh} onChange={(e) => setName(e.target.value)} disabled={!onDecide} />
      </label>
      <label>{t.commentApprove}
        <textarea value={comment} maxLength={4000} placeholder={t.commentPh} onChange={(e) => setComment(e.target.value)} disabled={!onDecide} />
      </label>
      {note && <p className={view.rounds?.next_extra ? "notice" : "source-note"}>{note}</p>}
      {view.rounds?.next_extra && (
        <label className="tk-check">
          <input type="checkbox" checked={extraAck} onChange={(e) => setExtraAck(e.target.checked)} disabled={!onDecide} />
          <span>{t.extraAck}</span>
        </label>
      )}
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      <div className="tk-marke-aktionen">
        <button className="primary" type="submit" disabled={!onDecide || busy !== null} aria-busy={busy === "approve"}>{t.approve}</button>
        <button type="button" disabled={!onDecide || busy !== null} aria-busy={busy === "changes"} onClick={() => void senden(false)}>{t.changes}</button>
      </div>
    </form>
  );
}

/** Datei ansehen (Bild inline, sonst im neuen Fenster) oder herunterladen; geladen erst auf Klick, jeder Abruf protokolliert. */
export function DateiZeile({ file, lang, load }: { file: ReviewFile; lang: Lang; load: (f: ReviewFile) => Promise<Blob> }) {
  const t = T[lang];
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(false);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const bild = file.mime.startsWith("image/") && file.mime !== "image/heic";
  const holen = async (): Promise<string | null> => {
    if (url) return url;
    setBusy(true);
    setFehler(false);
    try {
      const blob = await load(file);
      const u = URL.createObjectURL(blob);
      setUrl(u);
      return u;
    } catch {
      setFehler(true);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <div className="tk-marke-datei">
        <span>{file.name} · {fileSize(file.size, lang)}</span>
        <div className="row">
          {!url && <button type="button" disabled={busy} aria-busy={busy} onClick={() => void holen().then((u) => { if (u && !bild) window.open(u, "_blank", "noopener"); })}>{t.open}</button>}
          {url && !bild && <a className="btn" href={url} target="_blank" rel="noopener noreferrer">{t.open}</a>}
          {url && <a className="btn" href={url} download={file.name}>{t.download}</a>}
        </div>
      </div>
      {url && bild && <img className="tk-marke-bild" src={url} alt={file.name} />}
      {fehler && <p className="tk-error" role="alert">{t.fileError}</p>}
    </div>
  );
}
